<template>
  <aside class="app-development-cta" :dir="isArabic ? 'rtl' : 'ltr'" :aria-labelledby="headingId">
    <div class="cta-content">
      <div class="cta-decoration" aria-hidden="true">
        <span class="cta-ring ring-one"></span><span class="cta-ring ring-two"></span>
        <span class="glow-line"></span><span class="dot-grid"></span>
      </div>
      <div class="cta-copy">
        <span class="cta-label">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 9h16l-1 11H5L4 9Zm2-5h12l2 5H4l2-5Zm3 0V2m6 2V2"/></svg>
          {{ content.label }}
        </span>
        <h2 :id="headingId">{{ content.title }} <span>{{ content.titleAccent }}</span></h2>
        <p>{{ content.description }}</p>
        <ul class="feature-list" :aria-label="content.featuresLabel">
          <li v-for="feature in content.features" :key="feature"><span aria-hidden="true">✓</span>{{ feature }}</li>
        </ul>
        <a :href="content.url" @click="trackClick">
          {{ content.button }}
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path :d="isArabic ? 'M5 12h14m-6-6 6 6-6 6' : 'M19 12H5m6-6-6 6 6 6'"/></svg>
        </a>
        <small><span aria-hidden="true">●</span>{{ content.trust }}</small>
      </div>
      <div class="cta-visual" aria-hidden="true">
        <span class="visual-glow"></span>
        <div class="product-card"><span>🕶️</span><b>{{ content.product }}</b><small>199 {{ content.currency }}</small><i>+</i></div>
        <div class="phone">
          <div class="phone-screen">
            <div class="phone-top"><div><small>{{ content.hello }}</small><strong>{{ content.store }}</strong></div><span>🔔<i>3</i></span></div>
            <div class="phone-search">⌕ {{ content.search }}</div>
            <div class="phone-banner"><b>{{ content.collection }}</b><small>{{ content.discount }}</small></div>
            <b class="section-label">{{ content.categories }}</b>
            <div class="categories"><span>👕</span><span>👟</span><span>⌚</span><span>👜</span></div>
            <b class="section-label">{{ content.featured }}</b>
            <div class="products"><span>⌚<b>{{ content.watch }}</b></span><span>👜<b>{{ content.bag }}</b></span></div>
            <div class="phone-nav"><span>◉</span><span>♡</span><span>⌑</span><span class="active">⌂</span></div>
          </div>
        </div>
        <span class="floating-card card-notifications"><strong>🔔</strong>{{ content.notificationCard }}</span>
        <span class="floating-card card-platforms"><strong>iOS</strong><b>+</b><strong>Android</strong></span>
      </div>
    </div>
  </aside>
</template>

<script>
import { trackPromotionClick } from '@/utils/promotionAnalytics'

const CONTENT = {
  en: {
    label: 'Your store, one tap away',
    title: 'Turn Your Store Into a',
    titleAccent: 'Professional Mobile App',
    description: 'Launch a polished iOS and Android shopping app within 48 hours, designed around your brand and ready to keep customers connected.',
    featuresLabel: 'What is included',
    features: ['Your brand design', 'Ready in 48 hours', 'iOS & Android'],
    button: 'Build Your Mobile App',
    trust: 'No technical experience needed — we handle everything',
    notificationCard: 'Reach customers instantly',
    hello: 'Hello,', store: 'My Store', search: 'Search products...', collection: 'New Collection', discount: 'Up to 30% off',
    categories: 'Categories', featured: 'Featured products', watch: 'Classic watch', bag: 'Luxury bag', product: 'Sunglasses', currency: 'SAR',
    url: 'https://splendapp.com/en/'
  },
  ar: {
    label: 'متجرك على بُعد ضغطة',
    title: 'حوّل متجرك إلى',
    titleAccent: 'تطبيق جوال احترافي',
    description: 'أطلق تطبيق تسوق احترافياً على iOS وAndroid خلال 48 ساعة، بتصميم يعكس هويتك ويبقي عملاءك على تواصل دائم.',
    featuresLabel: 'ما الذي ستحصل عليه',
    features: ['تصميم يعكس هويتك', 'جاهز خلال 48 ساعة', 'iOS وAndroid'],
    button: 'أنشئ تطبيق متجرك',
    trust: 'لا تحتاج إلى خبرة تقنية — نحن نتولى كل شيء',
    notificationCard: 'تواصل مع عملائك فوراً',
    hello: 'مرحباً،', store: 'متجري', search: 'ابحث عن المنتجات...', collection: 'تشكيلة جديدة', discount: 'خصم حتى 30%',
    categories: 'الأقسام', featured: 'منتجات مميزة', watch: 'ساعة كلاسيكية', bag: 'حقيبة فاخرة', product: 'نظارة شمسية', currency: 'ر.س',
    url: 'https://splendapp.com/'
  }
}

export default {
  name: 'AppDevelopmentCta',
  computed: {
    isArabic() { return this.$store.state.currentLanguage === 'ar' },
    content() { return CONTENT[this.isArabic ? 'ar' : 'en'] },
    headingId() { return `app-development-cta-${this.isArabic ? 'ar' : 'en'}` }
  },
  methods: {
    trackClick() {
      trackPromotionClick(this.$gtag, 'app_development_cta', {
        language: this.isArabic ? 'ar' : 'en',
        source_route: this.$route.name || 'unknown'
      })
    }
  }
}
</script>

<style scoped>
.app-development-cta { width: 100%; max-width: 100%; padding: clamp(38px, 6vw, 76px) clamp(16px, 5vw, 56px); overflow: hidden; background: var(--page-surface, #f6f9fb); }
.cta-content { position: relative; display: grid; grid-template-columns: minmax(0, 1.08fr) minmax(250px, .92fr); align-items: center; width: min(100%, 980px); min-height: 410px; margin-inline: auto; padding: clamp(30px, 5vw, 50px); padding-inline-end: clamp(12px, 2vw, 24px); overflow: hidden; border: 1px solid rgba(78,139,255,.2); border-radius: 34px; background: radial-gradient(circle at 78% 44%, rgba(47,116,255,.28), transparent 34%), linear-gradient(125deg, #071a3b 0%, #0a285a 55%, #071a3b 100%); color: #fff; text-align: start; box-shadow: 0 24px 60px rgba(7,26,59,.2); }
.cta-decoration { position: absolute; inset: 0; pointer-events: none; }
.cta-ring { position: absolute; border: 1px solid rgba(95,150,255,.16); border-radius: 50%; }
.ring-one { width: 360px; height: 360px; inset-inline-end: -75px; top: -105px; }
.ring-two { width: 230px; height: 230px; inset-inline-end: 40px; bottom: -125px; }
.glow-line { position: absolute; inset: 0 auto auto 12%; width: 36%; height: 1px; background: linear-gradient(90deg, transparent, #3b82f6, transparent); box-shadow: 0 0 16px #3b82f6; }
.dot-grid { position: absolute; inset-inline-start: 3%; bottom: 6%; width: 70px; height: 45px; opacity: .2; background-image: radial-gradient(#8db5ff 1.3px, transparent 1.3px); background-size: 11px 11px; }
.cta-copy { position: relative; z-index: 3; }
.cta-label { display: inline-flex; align-items: center; gap: 8px; margin-bottom: 13px; padding: 7px 12px; border: 1px solid rgba(93,153,255,.38); border-radius: 999px; background: rgba(35,99,210,.17); color: #dce9ff; font-size: .76rem; font-weight: 900; }
.cta-label svg { width: 17px; height: 17px; }
.cta-content h2 { margin: 0 0 14px; color: inherit; font-size: clamp(1.7rem, 4vw, 2.45rem); font-weight: 900; line-height: 1.22; }
.cta-content h2 span { display: block; color: #5590ff; }
.cta-content p { max-width: 590px; margin: 0 0 18px; color: rgba(231,239,255,.82); font-size: clamp(.94rem, 1.8vw, 1.06rem); line-height: 1.7; }
.feature-list { display: flex; flex-wrap: wrap; gap: 8px; margin: 0 0 22px; padding: 0; list-style: none; }
.feature-list li { display: flex; align-items: center; gap: 6px; padding: 7px 10px; border: 1px solid rgba(255,255,255,.12); border-radius: 999px; background: rgba(255,255,255,.07); color: #edf4ff; font-size: .76rem; font-weight: 800; }
.feature-list li span { color: #65a3ff; }
.cta-content a { display: flex; align-items: center; justify-content: center; gap: 10px; width: fit-content; min-height: 54px; padding: 11px 22px; border: 1px solid #70a4ff; border-radius: 17px; background: linear-gradient(135deg, #3079f5, #1757cf); color: #fff; font-size: clamp(.96rem, 2vw, 1.08rem); font-weight: 900; line-height: 1.4; text-decoration: none; box-shadow: 0 12px 28px rgba(25,88,207,.34); transition: transform .18s ease, box-shadow .18s ease, filter .18s ease; }
.cta-content a:hover { transform: translateY(-2px); filter: brightness(1.08); box-shadow: 0 15px 32px rgba(25,88,207,.45); }
.cta-content a:focus-visible { outline: 4px solid #fbbf24; outline-offset: 4px; }
.cta-content svg { flex: 0 0 auto; width: 22px; height: 22px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.cta-content small { display: flex; align-items: center; gap: 7px; margin-top: 13px; color: rgba(222,234,255,.66); font-size: .7rem; }
.cta-content small span { color: #34d399; font-size: .55rem; }
.cta-visual { position: relative; align-self: stretch; min-height: 310px; }
.visual-glow { position: absolute; width: 320px; height: 320px; inset-inline-start: 50%; inset-block-start: 50%; border-radius: 50%; background: radial-gradient(circle, rgba(60,128,255,.42), rgba(30,86,188,.08) 58%, transparent 72%); transform: translate(-50%, -48%); }
[dir='rtl'] .visual-glow { transform: translate(50%, -48%); }
.phone { position: absolute; z-index: 2; width: 190px; height: 365px; inset-inline-start: 50%; top: 8px; padding: 7px; border: 2px solid #7083a8; border-radius: 31px; background: linear-gradient(145deg,#1d2739,#080d17); box-shadow: 0 25px 38px rgba(0,0,0,.5); transform: translateX(-50%) rotate(4deg); }
[dir='rtl'] .phone { transform: translateX(50%) rotate(4deg); }
.phone::before { content: ''; position: absolute; z-index: 4; width: 65px; height: 17px; left: 50%; top: 7px; border-radius: 0 0 12px 12px; background: #070b13; transform: translateX(-50%); }
.phone-screen { position: relative; height: 100%; overflow: hidden; padding: 25px 12px 40px; border-radius: 24px; background: #f5f6f8; color: #192034; font-size: 7px; }
.phone-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 9px; }
.phone-top div { display: flex; flex-direction: column; }
.phone-top small { color: #8b91a0; }
.phone-top strong { font-size: 11px; }
.phone-top > span { position: relative; display: grid; width: 24px; height: 24px; place-items: center; border-radius: 50%; background: #fff; box-shadow: 0 2px 7px rgba(0,0,0,.08); }
.phone-top i { position: absolute; top: -3px; right: -2px; display: grid; width: 10px; height: 10px; place-items: center; border-radius: 50%; background: #ef4444; color: #fff; font-size: 6px; font-style: normal; }
.phone-search { margin-bottom: 9px; padding: 7px 9px; border-radius: 8px; background: #e8eaf0; color: #9499a5; }
.phone-banner { display: flex; flex-direction: column; height: 68px; margin-bottom: 9px; padding: 13px; border-radius: 12px; background: linear-gradient(135deg,#145dda,#51a4ff); color: #fff; }
.phone-banner b { font-size: 11px; }
.phone-banner small { margin-top: 5px; }
.section-label { display: block; margin: 7px 0 5px; font-size: 8px; }
.categories { display: grid; grid-template-columns: repeat(4,1fr); gap: 5px; }
.categories span { display: grid; height: 29px; place-items: center; border-radius: 8px; background: #fff; font-size: 13px; }
.products { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.products span { display: flex; flex-direction: column; align-items: center; padding: 8px 4px; border-radius: 9px; background: #fff; font-size: 21px; }
.products b { margin-top: 4px; font-size: 6px; }
.phone-nav { position: absolute; inset: auto 0 0; display: grid; grid-template-columns: repeat(4,1fr); height: 36px; place-items: center; border-top: 1px solid #e3e5ea; background: #fff; font-size: 12px; }
.phone-nav .active { color: #2878f5; }
.product-card { position: absolute; z-index: 4; inset-inline-start: -1px; top: 130px; display: flex; flex-direction: column; width: 92px; padding: 9px; border-radius: 14px; background: #fff; color: #1b2538; box-shadow: 0 14px 28px rgba(0,0,0,.28); transform: rotate(-7deg); }
[dir='rtl'] .product-card { transform: rotate(7deg); }
.product-card > span { display: grid; height: 65px; place-items: center; border-radius: 10px; background: #edf3ff; font-size: 31px; }
.product-card b { margin-top: 6px; font-size: 8px; }
.product-card small { font-size: 8px; font-weight: 900; }
.product-card i { position: absolute; inset-inline-end: 7px; bottom: 7px; display: grid; width: 20px; height: 20px; place-items: center; border-radius: 50%; background: #2878f5; color: #fff; font-style: normal; }
.floating-card { position: absolute; z-index: 4; display: flex; align-items: center; gap: 7px; padding: 9px 11px; border: 1px solid rgba(255,255,255,.18); border-radius: 12px; background: rgba(7,25,58,.78); color: #e8f0ff; font-size: .68rem; font-weight: 800; box-shadow: 0 12px 25px rgba(0,0,0,.28); backdrop-filter: blur(9px); }
.card-notifications { inset-inline-start: 2px; top: 56px; }
.card-notifications strong { color: #34d399; }
.card-platforms { inset-inline-end: 5px; bottom: 38px; }
.card-platforms strong { color: #71a5ff; }
.card-platforms b { color: rgba(255,255,255,.48); }
@media (max-width: 760px) {
  .cta-content { grid-template-columns: 1fr; min-height: auto; padding: 34px 24px 0; text-align: center; }
  .cta-content p { margin-inline: auto; }
  .feature-list { justify-content: center; }
  .cta-content a { width: 100%; }
  .cta-content small { justify-content: center; }
  .cta-visual { min-height: 280px; margin-top: 4px; }
  .phone { top: 5px; }
  .card-notifications { inset-inline-start: 5%; top: 65px; }
  .card-platforms { inset-inline-end: 4%; bottom: 35px; }
}
@media (max-width: 520px) {
  .app-development-cta { padding-inline: 12px; }
  .cta-content { padding: 28px 18px 0; border-radius: 24px; }
  .cta-content a { min-height: 52px; border-radius: 15px; }
  .cta-visual { min-height: 235px; }
  .phone { top: -8px; transform: translateX(-50%) rotate(4deg) scale(.78); transform-origin: top center; }
  [dir='rtl'] .phone { transform: translateX(50%) rotate(4deg) scale(.78); }
  .product-card { top: 90px; transform: rotate(-7deg) scale(.82); }
  [dir='rtl'] .product-card { transform: rotate(7deg) scale(.82); }
  .floating-card { padding: 7px 9px; font-size: .6rem; }
  .card-notifications { top: 48px; }
  .ring-one { width: 280px; height: 280px; }
}
@media (prefers-reduced-motion: reduce) { .cta-content a { transition: none; } }
</style>
